import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Alert,
  IconButton,
  LinearProgress,
  Chip,
  Grid,
} from '@mui/material';
import { Close, Add, CheckCircle, Circle } from '@mui/icons-material';
import { useLocation } from 'react-router';
import { get_custom_goal_detail, get_goal_entries } from 'src/auth/context/jwt/program';
import AddEntryDialog from './AddGoalEntryDialogue';
import { EntryList } from './EntryList';

const GoalDetailScreen = () => {
  const [showAlert, setShowAlert] = useState(true);
  const [addEntryOpen, setAddEntryOpen] = useState(false);
  const location = useLocation();
  const goal_id = location.state?.goal_id;

  const [loading_goal, setloading_goal] = useState(false);
  const [goal_data, setgoal_data] = useState(null);
  const [goalEntries, setgoalEntries] = useState([]);

  const get_goal_details = async () => {
    setloading_goal(true);

    try {
      const res = await get_custom_goal_detail({ id: goal_id });
      setgoal_data(res?.data?.data);
    } catch (error) {
      console.log(typeof error === 'string' ? error : error?.error);
    } finally {
      setloading_goal(false);
    }
  };

  const get_custom_goal_entries = async () => {
    setloading_goal(true);

    try {
      const res = await get_goal_entries({ goal_id });
      setgoalEntries(res?.data?.data);
    } catch (error) {
      console.log(typeof error === 'string' ? error : error?.error);
    } finally {
      setloading_goal(false);
    }
  };

  // Fetch data based on goalId
  useEffect(() => {
    if (goal_id) {
      get_goal_details();
      get_custom_goal_entries();
    }
    // eslint-disable-next-line
  }, [goal_id]);

  const handleEntryRefresh = () => {
    // Refresh goal data after adding a new entry
    get_goal_details();
    get_custom_goal_entries();
  };

  const isCategorical = goal_data?.category?.name.toLowerCase() === 'categorical';

  const getCompletedMilestones = () => {
    return goal_data?.completed_milestone_count || 0;
  };

  const getTotalMilestones = () => {
    return goal_data?.total_milestone_count || 0;
  };

  const ProgressStepper = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Progress Tracker
          </Typography>
          <Chip
            label={`${getCompletedMilestones()}/${getTotalMilestones()} completed`}
            color="primary"
            variant="outlined"
          />
        </Box>

        <Stepper orientation="vertical">
          {goal_data?.milestones?.map((milestone, index) => (
            <Step key={milestone.id} completed={milestone.is_completed}>
              <StepLabel
                StepIconComponent={milestone.is_completed ? CheckCircle : Circle}
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: '1rem',
                  },
                }}
              >
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {isCategorical
                      ? milestone.description || `Milestone ${index + 1}`
                      : `Reach ${milestone.value} ${milestone.unit?.name || ''}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isCategorical
                      ? 'Achievement milestone'
                      : `Target: ${milestone.value} ${milestone.unit?.name || ''}`}
                  </Typography>
                </Box>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary">
                  {milestone.is_completed ? 'Completed! 🎉' : 'In progress...'}
                </Typography>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </CardContent>
    </Card>
  );

  // Show loading state
  if (loading_goal) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Typography>Loading goal details...</Typography>
      </Box>
    );
  }

  // Show error state if no goal data
  if (!goal_data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No goal data found. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Alert Note */}
      {showAlert && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <IconButton size="small" onClick={() => setShowAlert(false)}>
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {`Your goals are here to guide and motivate you. Progress isn't always a straight line.
          Setbacks are a normal part of the journey. Celebrate every step forward, stay consistent,
          and remember that even small efforts add up to big results. Keep going. You've got this!`}
        </Alert>
      )}

      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {goal_data.title}
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setAddEntryOpen(true)}>
            Add Entry
          </Button>
        </Box>

        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Chip
            label={goal_data.category?.name}
            color={isCategorical ? 'secondary' : 'primary'}
            variant="filled"
          />
          <Typography variant="body2" color="text.secondary">
            {isCategorical
              ? goal_data.end_goal
              : `Starting from ${goal_data.starting_value} ${goal_data.unit?.name}`}
          </Typography>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 3 }}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Overall Progress
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {goal_data.progress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={parseFloat(goal_data.progress) || 0}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <ProgressStepper />
        </Grid>
        <Grid item xs={12} md={4}>
          <EntryList goalEntries={goalEntries} isCategorical={isCategorical} goalData={goal_data} />
        </Grid>
      </Grid>

      <AddEntryDialog
        open={addEntryOpen}
        onClose={() => setAddEntryOpen(false)}
        onRefresh={handleEntryRefresh}
        goalData={goal_data}
      />
    </Box>
  );
};

export default GoalDetailScreen;
